import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const MyPostStateContext = createContext(null);

/**
 * sessionStorage에서 값을 가져오는 헬퍼 함수
 */
const getFromSessionStorage = (key, defaultValue) => {
  try {
    const value = sessionStorage.getItem(key);
    if (value === null) return defaultValue;
    
    // JSON 파싱 시도 (배열이나 객체인 경우)
    if (value.startsWith('[') || value.startsWith('{')) {
      return JSON.parse(value);
    }
    
    // boolean 처리
    if (value === 'true') return true;
    if (value === 'false') return false;
    
    return value;
  } catch (error) {
    console.error(`[MyPostStateContext] sessionStorage 읽기 실패 (${key}):`, error);
    return defaultValue;
  }
};

/**
 * sessionStorage에 값을 저장하는 헬퍼 함수
 */
const saveToSessionStorage = (key, value) => {
  try {
    if (typeof value === 'object') {
      sessionStorage.setItem(key, JSON.stringify(value));
    } else {
      sessionStorage.setItem(key, String(value));
    }
  } catch (error) {
    console.error(`[MyPostStateContext] sessionStorage 저장 실패 (${key}):`, error);
  }
};

const resolveNextValue = (value, previousValue) => (
  typeof value === 'function' ? value(previousValue) : value
);

const isSameStateValue = (a, b) => {
  if (Object.is(a, b)) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((item, index) => Object.is(item, b[index]));
};

const useLoggedStateSetter = (setter, label) => (
  useCallback((value) => {
    setter((previousValue) => {
      const nextValue = resolveNextValue(value, previousValue);
      if (isSameStateValue(previousValue, nextValue)) return previousValue;
      console.log(`[MyPostStateContext] ${label} 변경:`, nextValue);
      return nextValue;
    });
  }, [setter, label])
);

export const MyPostStateProvider = ({ children }) => {
  // 모드 상태
  const [isLocalMode, setIsLocalModeState] = useState(() => 
    getFromSessionStorage('mypost:isLocalMode', false)
  );

  // 온라인 모드 상태
  const [viewMode, setViewModeState] = useState(() => 
    getFromSessionStorage('mypost:viewMode', 'column')
  );
  
  const [filterMode, setFilterModeState] = useState(() => 
    getFromSessionStorage('mypost:filterMode', 'all')
  );
  
  const [sortColumn, setSortColumnState] = useState(() => 
    getFromSessionStorage('mypost:sortColumn', 'timestamp')
  );
  
  const [sortOrder, setSortOrderState] = useState(() => 
    getFromSessionStorage('mypost:sortOrder', 'DESC')
  );
  
  const [selectedPath, setSelectedPathState] = useState(() => 
    getFromSessionStorage('mypost:selectedPath', [])
  );

  // 로컬 모드 상태
  const [localSelectedPath, setLocalSelectedPathState] = useState(() =>
    getFromSessionStorage('mypost:localSelectedPath', [])
  );

  // isLocalMode 변경 시 sessionStorage 동기화
  useEffect(() => {
    saveToSessionStorage('mypost:isLocalMode', isLocalMode);
  }, [isLocalMode]);

  // viewMode 변경 시 sessionStorage 동기화
  useEffect(() => {
    saveToSessionStorage('mypost:viewMode', viewMode);
  }, [viewMode]);

  // filterMode 변경 시 sessionStorage 동기화
  useEffect(() => {
    saveToSessionStorage('mypost:filterMode', filterMode);
  }, [filterMode]);

  // sortColumn 변경 시 sessionStorage 동기화
  useEffect(() => {
    saveToSessionStorage('mypost:sortColumn', sortColumn);
  }, [sortColumn]);

  // sortOrder 변경 시 sessionStorage 동기화
  useEffect(() => {
    saveToSessionStorage('mypost:sortOrder', sortOrder);
  }, [sortOrder]);

  // selectedPath 변경 시 sessionStorage 동기화
  useEffect(() => {
    saveToSessionStorage('mypost:selectedPath', selectedPath);
  }, [selectedPath]);

  // localSelectedPath 변경 시 sessionStorage 동기화
  useEffect(() => {
    saveToSessionStorage('mypost:localSelectedPath', localSelectedPath);
  }, [localSelectedPath]);

  // 래퍼 함수들 (로깅 및 검증 추가 가능)
  const setIsLocalMode = useLoggedStateSetter(setIsLocalModeState, 'isLocalMode');
  const setViewMode = useLoggedStateSetter(setViewModeState, 'viewMode');
  const setFilterMode = useLoggedStateSetter(setFilterModeState, 'filterMode');
  const setSortColumn = useLoggedStateSetter(setSortColumnState, 'sortColumn');
  const setSortOrder = useLoggedStateSetter(setSortOrderState, 'sortOrder');
  const setSelectedPath = useLoggedStateSetter(setSelectedPathState, 'selectedPath');
  const setLocalSelectedPath = useLoggedStateSetter(setLocalSelectedPathState, 'localSelectedPath');

  // 모든 상태 초기화
  const resetState = useCallback(() => {
    console.log('[MyPostStateContext] 상태 초기화');
    setIsLocalModeState(false);
    setViewModeState('column');
    setFilterModeState('all');
    setSortColumnState('timestamp');
    setSortOrderState('DESC');
    setSelectedPathState([]);
    setLocalSelectedPathState([]);
    
    // sessionStorage도 초기화
    sessionStorage.removeItem('mypost:isLocalMode');
    sessionStorage.removeItem('mypost:viewMode');
    sessionStorage.removeItem('mypost:filterMode');
    sessionStorage.removeItem('mypost:sortColumn');
    sessionStorage.removeItem('mypost:sortOrder');
    sessionStorage.removeItem('mypost:selectedPath');
    sessionStorage.removeItem('mypost:localSelectedPath');
  }, []);

  const value = useMemo(() => ({
    // 모드 상태
    isLocalMode,
    setIsLocalMode,
    
    // 온라인 모드 상태
    viewMode,
    setViewMode,
    filterMode,
    setFilterMode,
    sortColumn,
    setSortColumn,
    sortOrder,
    setSortOrder,
    selectedPath,
    setSelectedPath,
    
    // 로컬 모드 상태
    localSelectedPath,
    setLocalSelectedPath,
    
    // 유틸리티
    resetState
  }), [
    isLocalMode,
    setIsLocalMode,
    viewMode,
    setViewMode,
    filterMode,
    setFilterMode,
    sortColumn,
    setSortColumn,
    sortOrder,
    setSortOrder,
    selectedPath,
    setSelectedPath,
    localSelectedPath,
    setLocalSelectedPath,
    resetState
  ]);

  return (
    <MyPostStateContext.Provider value={value}>
      {children}
    </MyPostStateContext.Provider>
  );
};

export const useMyPostState = () => {
  const context = useContext(MyPostStateContext);
  if (!context) {
    throw new Error('useMyPostState must be used within MyPostStateProvider');
  }
  return context;
};

